import { Cell, GameState, Item, GameMode, PersistentCell } from './types';
import { debounce } from './utils';

// 存储键名
export const STORAGE_KEYS = {
  GAME_GRID: 'numbergame:grid',
  GAME_STATE: 'numbergame:gameState',
  MOVES: 'numbergame:moves',
  ITEMS: 'numbergame:items',
  LANGUAGE: 'numbergame:language',
  DIAMONDS: 'numbergame:diamonds',
  SOUND_ON: 'numbergame:soundOn'
};

// 持久化配置
export interface PersistConfig {
  debounceTime: number; // 防抖时间 (ms)
}

// 默认配置
const DEFAULT_CONFIG: PersistConfig = {
  debounceTime: 1000 // 默认1秒防抖
};

// 存储数据结构
export interface GamePersistData {
  grid: PersistentCell[][]; 
  gameState: GameState;
  moves: number;
  items: Item[];
  language: string;
  diamonds: number;
  soundOn: boolean;
}

/**
 * 游戏数据持久化服务
 */
class StorageService {
  private config: PersistConfig;
  private saveDataDebounced: Function;

  constructor(config: Partial<PersistConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.saveDataDebounced = debounce(this.saveData.bind(this), this.config.debounceTime);
  }

  /**
   * 保存所有游戏数据（带防抖）
   */
  public persistData(data: Partial<GamePersistData>): void {
    this.saveDataDebounced(data);
  }

  /**
   * 保存所有游戏数据（不带防抖，直接保存）
   */
  public saveData(data: Partial<GamePersistData>): void {
    try {
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined) {
          // 直接映射键名
          let storageKey: string;
          switch(key) {
            case 'grid':
              storageKey = STORAGE_KEYS.GAME_GRID;
              break;
            case 'gameState':
              storageKey = STORAGE_KEYS.GAME_STATE;
              break;
            case 'moves':
              storageKey = STORAGE_KEYS.MOVES;
              break;
            case 'items':
              storageKey = STORAGE_KEYS.ITEMS;
              break;
            case 'language':
              storageKey = STORAGE_KEYS.LANGUAGE;
              break;
            case 'diamonds':
              storageKey = STORAGE_KEYS.DIAMONDS;
              break;
            case 'soundOn':
              storageKey = STORAGE_KEYS.SOUND_ON;
              break;
            default:
              storageKey = '';
          }
          
          if (storageKey) {
            try {
              const jsonData = JSON.stringify(value);
              
              // 检查是否是网格数据，特别记录
              if (key === 'grid') {
                const gridData = value as PersistentCell[][];
              }
              
              localStorage.setItem(storageKey, jsonData);
              // console.log(`StorageService: Successfully saved ${key}`);
              
              // 验证存储
              const verify = localStorage.getItem(storageKey);
              if (verify) {
                // console.log(`StorageService: Storage verified for ${key}, length: ${verify.length} bytes`);
              } else {
                // console.error(`StorageService: Failed to verify storage for ${key}`);
              }
            } catch (innerError) {
              console.error(`StorageService: Error saving ${key}:`, innerError);
            }
          } else {
            console.warn(`StorageService: No storage key found for ${key}`);
          }
        }
      });
    } catch (error) {
      console.error('StorageService: Error in saveData:', error);
    }
  }

  /**
   * 加载游戏数据
   */
  public loadData(): Partial<GamePersistData> {
    try {
      const result: Partial<GamePersistData> = {};
      
      // 读取网格数据
      const gridData = localStorage.getItem(STORAGE_KEYS.GAME_GRID);
      if (gridData) {
        result.grid = JSON.parse(gridData);
      }
      
      // 读取游戏状态
      const gameStateData = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
      if (gameStateData) {
        result.gameState = JSON.parse(gameStateData);
      }
      
      // 读取行动点
      const movesData = localStorage.getItem(STORAGE_KEYS.MOVES);
      if (movesData) {
        result.moves = JSON.parse(movesData);
      }
      
      // 读取道具
      const itemsData = localStorage.getItem(STORAGE_KEYS.ITEMS);
      if (itemsData) {
        result.items = JSON.parse(itemsData);
      }
      
      // 读取语言设置
      const languageData = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
      if (languageData) {
        result.language = JSON.parse(languageData);
      }
      
      // 读取钻石数量
      const diamondsData = localStorage.getItem(STORAGE_KEYS.DIAMONDS);
      if (diamondsData) {
        result.diamonds = JSON.parse(diamondsData);
      }
      
      // 读取声音设置
      const soundOnData = localStorage.getItem(STORAGE_KEYS.SOUND_ON);
      if (soundOnData) {
        result.soundOn = JSON.parse(soundOnData);
      }
      return result;
    } catch (error) {
      console.error('StorageService: Error loading game data:', error);
      return {};
    }
  }

  /**
   * 清除所有游戏数据
   */
  public clearAll(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * 更新配置
   */
  public updateConfig(config: Partial<PersistConfig>): void {
    this.config = { ...this.config, ...config };
    // 重新创建防抖函数
    this.saveDataDebounced = debounce(this.saveData.bind(this), this.config.debounceTime);
  }
}

// 导出单例实例
export const storageService = new StorageService();
export default storageService; 